import Editor from '@monaco-editor/react';
import { FileCopy } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  Link,
  Radio,
  RadioGroup,
  Theme,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useI18n } from '../../../../../../application/language/useI18n';
import { ICodeExample } from '../../../../../../service/passing/interface/IPassingQuiz';
import { IQuestion } from '../../../../../../service/unit/interface/IUnitQuiz';
import { IQuizQuestionsResult } from '../../../../../../service/unitResult/IUnitResultModel';
import { IPassingQuizDomain } from './store/IPassingQuizDomain';
import { Colors } from '../../../../../design/color/Colors';
import { CUSTOM_THEME_NAME, defineAppTheme } from '../../../../../design/codeEditor/monacoTheme';

export interface IPassingQuestionProperties {
  question: IQuestion;
  answer?: IQuizQuestionsResult;
  rootDomain?: IPassingQuizDomain;
  index: number;
}

export const PassingQuestion = observer(({ question, answer, rootDomain, index }: IPassingQuestionProperties) => {
  const { translate } = useI18n();
  const [currentCode, setCurrentCode] = useState<ICodeExample | null>(
    question.codeExample && question.codeExample.length > 0 ? question.codeExample[0] : null,
  );

  const [editorHeight, setEditorHeight] = useState<number>(0);
  const [selectedCheckboxes, setSelectedCheckboxes] = useState<string[]>([]);
  const [radioValue, setRadioValue] = useState<string>('');

  const maxHeight = 272;
  const editorRef = useRef(null as any);

  const handleEditorMount = (editor) => {
    const model = editor.getModel()
    if(model) {
      setTimeout(() => {
        editorRef.current = editor;
        setEditorHeight(model.getLineCount() * 18);
        editor.layout();
      }, 0);
    }
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const isChecked = event.target.checked;
    let newSelectedCheckboxes: string[];

    if (isChecked) {
      newSelectedCheckboxes = [...selectedCheckboxes, value];
    } else {
      newSelectedCheckboxes = selectedCheckboxes.filter((item) => item !== value);
    }

    setSelectedCheckboxes(newSelectedCheckboxes);
    rootDomain?.addAnswer(newSelectedCheckboxes, question.pseudoId);
  };
  const useStyles = makeStyles((theme: Theme) => ({
    editor: {
      maxHeight: '272px',
      height: editorHeight > maxHeight ? '100%' : editorHeight,
      '& .monaco-editor .overflow-guard': {
        height: editorHeight,
        borderRadius: '0px 0px 16px 16px',
      },
    },
  }));
  const defaultAnswer: Pick<IQuizQuestionsResult, 'answersIds' | 'result'> = {
    answersIds: [],
    result: 0,
  };

  const effectiveAnswer = answer || defaultAnswer;
  const classes = useStyles();
  const handleClick = useCallback((code) => {
    setCurrentCode(code);
    handleEditorMount(editorRef.current);
  }, []);

  useEffect(() => {
    if (rootDomain?.ui?.isEbaleToRePass?.value === false && effectiveAnswer.answersIds.length > 0) {
      setRadioValue(effectiveAnswer.answersIds[0]);

      setSelectedCheckboxes(effectiveAnswer.answersIds.map((item) => item));
    }
  }, [effectiveAnswer, effectiveAnswer.answersIds, rootDomain?.ui?.isEbaleToRePass?.value]);

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden' }}>
      <Typography variant="subtitle1" sx={{ color: Colors.textSecondary }}>
        {translate('phrases.question')} {index + 1}
      </Typography>
      <Typography variant="h6" sx={{ marginBottom: '16px', width: 'inherit', wordWrap: 'break-word', color: Colors.textPrimary }}>
        <Link
          sx={{ textDecoration: 'none', pointerEvents: 'none', cursor: 'none', color: 'inherit' }}
          href={`#${question.pseudoId}`}
          id={question.pseudoId}
        >
          {question.name}
        </Link>
      </Typography>
      {question.isCodeExample && (
        <Box>
          <Box sx={{ display: 'flex', gap: '20px', marginBottom: '16px' }}>
            {question.codeExample.map((code: ICodeExample) => (
              <Chip
                key={code.language}
                onClick={() => {
                  handleClick(code);
                }}
                label={code.name}
                sx={{ background: currentCode?.language === code.language ? Colors.primaryActive : '', cursor: 'pointer' }}
              />
            ))}
          </Box>
          <Box sx={{ marginBottom: '32px' }}>
            <Box
              sx={{
                borderRadius: '16px 16px 0px 0px',
                background: Colors.bgCode,
                padding: '4px 16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignSelf: 'center',
              }}
            >
              <Typography variant="body1">{currentCode?.name}</Typography>
              <IconButton
                sx={{ cursor: 'pointer', fontSize: '20px' }}
                onClick={() => {
                  currentCode && navigator.clipboard.writeText(currentCode?.value);
                }}
              >
                <FileCopy />
              </IconButton>
            </Box>
            {currentCode !== null && (
              <Editor
                key={currentCode?.language}
                className={classes.editor}
                theme={CUSTOM_THEME_NAME}
                beforeMount={defineAppTheme}
                height={editorHeight > maxHeight ? maxHeight : editorHeight}
                language={currentCode?.language}
                value={currentCode?.value}
                onMount={handleEditorMount}
                options={{
                  readOnly: true,
                  scrollbar: {
                    vertical: editorHeight > maxHeight ? 'auto' : 'hidden',
                    horizontal: 'hidden',
                    handleMouseWheel: editorHeight > 100 ? true : false,
                  },
                  minimap: { enabled: false },
                }}
              />
            )}
            <Box
              sx={{
                height: '14px',
                borderRadius: '0px 0px 16px 16px',
                background: Colors.bgCode,
              }}
            ></Box>
          </Box>
        </Box>
      )}
      {question.type === 'single' && (
        <FormControl>
          <RadioGroup
            sx={{ gap: '10px' }}
            onChange={(event) => {
              if (rootDomain?.ui?.isEbaleToCheck?.value) {
                setRadioValue(event.target.value);
                rootDomain.addAnswer([event.target.value], question.pseudoId);
              }
            }}
            value={radioValue}
          >
            {question.answers.map((answer) => (
              <FormControlLabel
                sx={{ display: 'flex', alignItems: 'flex-start' }}
                key={answer.pseudoId}
                value={answer.pseudoId}
                control={
                  <Radio
                    sx={{ marginTop: '-8.5px' }}
                    disabled={!rootDomain?.ui?.isEbaleToCheck?.value || !rootDomain?.ui?.isEbaleToRePass?.value}
                    data-cy={answer.answer}
                  />
                }
                label={answer.answer}
              />
            ))}
          </RadioGroup>
        </FormControl>
      )}
      {question.type === 'multi' && (
        <FormControl>
          <FormGroup sx={{ gap: '10px' }}>
            {question.answers.map((answer) => (
              <FormControlLabel
                sx={{ display: 'flex', alignItems: 'flex-start' }}
                key={answer.pseudoId}
                value={answer.pseudoId}
                control={
                  <Checkbox
                    sx={{ marginTop: '-8.5px' }}
                    value={answer.pseudoId}
                    onChange={handleCheckboxChange}
                    checked={selectedCheckboxes.includes(answer.pseudoId)}
                    disabled={!rootDomain?.ui?.isEbaleToCheck?.value || !rootDomain?.ui?.isEbaleToRePass.value}
                    data-cy={answer.answer}
                  />
                }
                label={answer.answer}
              />
            ))}
          </FormGroup>
        </FormControl>
      )}
      {(!rootDomain?.ui?.isEbaleToCheck?.value || !rootDomain?.ui?.isEbaleToRePass?.value) && (
        <Box sx={{ paddingTop: '16px' }}>
          <Box
            sx={{
              padding: '16px',
              backgroundColor: effectiveAnswer.result > 0.5 ? 'rgba(183, 229, 53, 0.1)' : 'rgba(229, 57, 53, 0.1)',
              borderRadius: '8px',
            }}
          >
            <Typography
              variant="h6"
              data-cy={question.type === 'multi' ? effectiveAnswer.answersIds[1] : effectiveAnswer.answersIds[0]}
            >
              {effectiveAnswer.result > 0.5 ? translate(`phrases.correctAnswer`) : translate(`phrases.wrongAnswer`)}
            </Typography>
            <Typography variant="body1">{question.explanation}</Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
});
